import React, { useState } from 'react';
import styled from 'styled-components';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import { Cancel, Close, CloseRounded } from '@mui/icons-material';

interface SeatIdentifierCellProps {
  seatIdentifier?: string;
  seatIdentifierType?: string;
  onSeatIdentifierChange?: (seatIdentifier: string | null, seatIdentifierType: string | null) => void;
  saving?: boolean;
}

const SeatIdentifierCell: React.FC<SeatIdentifierCellProps> = ({ 
  seatIdentifier, 
  seatIdentifierType, 
  onSeatIdentifierChange,
  saving
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localSeatIdentifier, setLocalSeatIdentifier] = useState(seatIdentifier || '');
  const [localSeatIdentifierType, setLocalSeatIdentifierType] = useState(seatIdentifierType || '');

  const handleClick = () => {
    if (saving) return;
    setIsEditing(true);
    setLocalSeatIdentifier(seatIdentifier || '');
    setLocalSeatIdentifierType(seatIdentifierType || '');
  };

  const handleSeatIdentifierChange = (value: string) => {
    setLocalSeatIdentifier(value);
  };

  const handleSeatIdentifierTypeChange = (value: string) => {
    setLocalSeatIdentifierType(value);
  };

  const handleSave = () => {
    if (onSeatIdentifierChange) {
      onSeatIdentifierChange(localSeatIdentifier, localSeatIdentifierType);
    }
    setIsEditing(false);
  };

  const handleClear = () => {
    if (onSeatIdentifierChange) {
      console.log('handleClear', null, null);
      onSeatIdentifierChange(null, null);
    }
    setIsEditing(false);
  };

  const handleBlur = () => {
    // Only save if both values are provided
    if (localSeatIdentifier && localSeatIdentifierType) {
      handleSave();
    } else {
      // setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <EditingContainer>
        <TextField
          size="small"
          value={localSeatIdentifier}
          onChange={(e) => handleSeatIdentifierChange(e.target.value)}
          onBlur={handleBlur}
          placeholder="Enter seat identifier"
          style={{ minWidth: '120px', marginRight: '8px' }}
          autoFocus
        />
        <FormControl size="small" style={{ minWidth: '100px', marginRight: '8px' }}>
          <Select
            value={localSeatIdentifierType}
            onChange={(e) => handleSeatIdentifierTypeChange(e.target.value)}
            onBlur={handleBlur}
            displayEmpty
          >
            <MenuItem value="">
              <em>Type</em>
            </MenuItem>
            <MenuItem value="incumbentName">Incumbent</MenuItem>
            <MenuItem value="seat">Seat</MenuItem>
            <MenuItem value="post">Post</MenuItem>
            <MenuItem value="division">Division</MenuItem>
            <MenuItem value="position">Position</MenuItem>
          </Select>
        </FormControl>
      </EditingContainer>
    );
  }

  // Display mode
  if (!seatIdentifier && !seatIdentifierType) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <EmptyContainer onClick={saving ? undefined : handleClick} $isSaving={saving || false}>
          <Typography variant="body2" style={{ color: '#666', fontStyle: 'italic', opacity: saving ? 0.6 : 1 }}>
            None
          </Typography>
        </EmptyContainer>
        {saving && (
          <CircularProgress size={16} style={{ marginLeft: '4px' }} />
        )}
      </div>
    );
  }

  let displayText = '';
  if (seatIdentifierType === 'incumbentName') {
    displayText = `Seat ${seatIdentifier || 'INCUMBENT'}`;
  } else if (seatIdentifierType === 'seat') {
    displayText = `Seat ${seatIdentifier || 'X'}`;
  } else if (seatIdentifierType === 'post') {
    displayText = `Post ${seatIdentifier || 'X'}`;
  } else if (seatIdentifierType === 'division') {
    displayText = `Division ${seatIdentifier || 'X'}`;
  } else if (seatIdentifierType === 'position') {
    displayText = `Post ${seatIdentifier || 'X'}`;
  } else {
    displayText = seatIdentifier || '';
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <SeatIdentifierContainer $isSaving={saving || false}>
        <Typography variant="body2" style={{ fontWeight: '500', opacity: saving ? 0.6 : 1 }} noWrap onClick={saving ? undefined : handleClick}>
          {displayText}
        </Typography>
        {!saving && (
          <IconButton 
            onClick={handleClear} 
            size="small"
            style={{ marginRight: '-8px' }}
          >
            <Cancel fontSize="small" style={{ fontSize: '16px' }} />
          </IconButton>
        )}
      </SeatIdentifierContainer>
      {saving && (
        <CircularProgress size={16} style={{ marginLeft: '4px' }} />
      )}
    </div>
  );
};

interface SeatIdentifierContainerProps {
  $isSaving?: boolean;
}

const SeatIdentifierContainer = styled.div<SeatIdentifierContainerProps>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background-color: #e3f2fd;
  border-radius: 12px;
  max-width: 200px;
  cursor: ${props => props.$isSaving ? 'not-allowed' : 'pointer'};
  gap: 4px;
  opacity: ${props => props.$isSaving ? 0.6 : 1};
  
  &:hover {
    background-color: ${props => props.$isSaving ? '#e3f2fd' : '#bbdefb'};
  }
`;

interface EmptyContainerProps {
  $isSaving?: boolean;
}

const EmptyContainer = styled.div<EmptyContainerProps>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  background-color: #f5f5f5;
  border: 1px dashed #ccc;
  border-radius: 12px;
  max-width: 200px;
  cursor: ${props => props.$isSaving ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$isSaving ? 0.6 : 1};
  
  &:hover {
    background-color: ${props => props.$isSaving ? '#f5f5f5' : '#e0e0e0'};
  }
`;

const EditingContainer = styled.div`
  display: inline-flex;
  align-items: center;
  max-width: 300px;
`;

export { SeatIdentifierCell };

